/**
 * Browser-side mirror of the desktop CoachService. Owns N parallel Claude
 * streams per conversation, emits the same `claude:event` shape the renderer
 * already consumes, supports image-multimodal asks, and translates auth /
 * rate-limit errors into actionable messages.
 *
 * Differences from `src/main/coach.ts`:
 *  - Uses the @anthropic-ai/sdk in `dangerouslyAllowBrowser` mode with the
 *    user's pasted key (no main-process keystore).
 *  - System prompt assembled from a local browser-safe copy (`system-prompt.ts`).
 *  - History lives in-memory only — IndexedDB persistence is layered on
 *    separately by the PWA shell (`src/pwa/db.ts`).
 */
import Anthropic from '@anthropic-ai/sdk';
import type { ClaudeEvent, ClaudeStartArgs } from '../../shared/ipc';
import { keys } from './keystore';
import { buildSystemPrompt, type CoachContext, type CoachMode } from './system-prompt';

/**
 * All Anthropic calls route through a same-origin proxy at `/api/anthropic`
 * to bypass org-level CORS restrictions. In dev, vite.pwa.config.ts forwards
 * the path to https://api.anthropic.com server-to-server. In production,
 * the host needs an equivalent serverless function (Vercel Edge / Netlify /
 * Cloudflare Worker) at the same path; without it, deployed PWA users will
 * see the same "CORS requests are not allowed for this Organization" error.
 *
 * The SDK rejects relative `baseURL` values (`new URL(path, base)` requires
 * an absolute base), so we resolve to `<origin>/api/anthropic` at module
 * load. `globalThis.location` is fine here — this module only runs in the
 * browser.
 *
 * The SDK still needs `dangerouslyAllowBrowser: true` because it checks for
 * a browser environment at construction time — that flag just disables the
 * guard; it doesn't change the request path.
 */
const PROXY_BASE_URL = `${globalThis.location.origin}/api/anthropic`;

interface Conversation {
  id: string;
  systemPrompt: string;
  history: Anthropic.MessageParam[];
  inflight: Map<string, AbortController>;
}

const conversations = new Map<string, Conversation>();
type Listener = (e: ClaudeEvent) => void;
const listeners = new Set<Listener>();

function emit(e: ClaudeEvent): void {
  for (const l of listeners) l(e);
}

/** Exposed for the OAI-compat dispatcher (`api/index.ts → startClaude`)
 *  so NVIDIA / Groq streams can publish to the same listener set as
 *  Anthropic streams. The renderer subscribes once via `onClaudeEvent`
 *  and receives every provider's events through that one channel. */
export function emitClaudeEvent(e: ClaudeEvent): void {
  emit(e);
}

function getOrCreateConversation(
  id: string,
  mode: CoachMode,
  context: CoachContext,
): Conversation {
  let convo = conversations.get(id);
  if (!convo) {
    convo = {
      id,
      systemPrompt: buildSystemPrompt(mode, context),
      history: [],
      inflight: new Map(),
    };
    conversations.set(id, convo);
  }
  return convo;
}

function abortAll(convo: Conversation): void {
  for (const ac of convo.inflight.values()) ac.abort();
  convo.inflight.clear();
}

function friendlyError(err: unknown): string {
  if (err instanceof Anthropic.APIError) {
    // Pull the underlying API error envelope (`{ type, error: { type, message } }`)
    // when present so the surfaced text says *why* the call failed
    // instead of a generic "invalid or expired".
    const payload = (err as unknown as { error?: { error?: { type?: string; message?: string } } }).error?.error;
    const detail = payload?.message ?? err.message;
    const innerType = payload?.type;
    if (err.status === 401) {
      return `Anthropic rejected the API key (401${innerType ? ` ${innerType}` : ''}): ${detail}. ` +
        'Re-paste the key in Settings → API & Model. If it works on desktop but not here, copy it directly from console.anthropic.com to avoid stray characters.';
    }
    if (err.status === 403) {
      return `Anthropic 403 (forbidden): ${detail}. Your key may not have access to this model — try a different model in Settings.`;
    }
    if (err.status === 404) {
      return `Anthropic 404: ${detail}. The model id may be wrong or unavailable to your account.`;
    }
    if (err.status === 429) {
      return `Anthropic rate-limited the request: ${detail}.`;
    }
    return `Anthropic API ${err.status ?? ''}${innerType ? ` ${innerType}` : ''}: ${detail}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/** ArrayBuffer → base64 (no Buffer in the browser). */
function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  // Chunk to avoid argument-count limits on large buffers.
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export async function start(args: ClaudeStartArgs): Promise<void> {
  const trimmed = args.question.trim();
  const images = args.imageJpegs ?? [];
  if (!trimmed && images.length === 0) return;

  const apiKey = keys.getAnthropic();
  if (!apiKey) {
    emit({
      type: 'error',
      conversationId: args.conversationId,
      exchangeId: args.exchangeId,
      message: 'No Anthropic API key set. Open Settings → API & Model to add one.',
    });
    return;
  }

  const convo = getOrCreateConversation(
    args.conversationId,
    (args.mode ?? 'auto') as CoachMode,
    {
      resume: args.profile?.resume,
      jobDescription: args.profile?.jobDescription,
      englishLevel: args.profile?.englishLevel,
    },
  );

  const userContent: Anthropic.ContentBlockParam[] = [];
  for (const img of images) {
    userContent.push({
      type: 'image',
      source: {
        type: 'base64',
        media_type: 'image/jpeg',
        data: bufferToBase64(img),
      },
    });
  }
  if (trimmed) {
    userContent.push({ type: 'text', text: trimmed });
  } else if (images.length > 1) {
    userContent.push({
      type: 'text',
      text:
        `These ${images.length} images are sequential parts of ONE scrollable question (top to bottom). ` +
        `Read all of them as one continuous prompt before answering. ` +
        `Produce a single answer that addresses the whole question — not one answer per image.`,
    });
  } else if (images.length === 1) {
    userContent.push({ type: 'text', text: 'Answer the question shown in the image.' });
  }

  const messages: Anthropic.MessageParam[] = [
    ...convo.history,
    { role: 'user', content: userContent },
  ];

  const abort = new AbortController();
  convo.inflight.set(args.exchangeId, abort);

  let assistantText = '';
  const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true, baseURL: PROXY_BASE_URL });

  try {
    const stream = client.messages.stream(
      {
        model: args.model ?? 'claude-sonnet-4-6',
        max_tokens: 1024,
        system: [
          {
            type: 'text',
            text: convo.systemPrompt,
            cache_control: { type: 'ephemeral' },
          },
        ],
        messages,
      },
      { signal: abort.signal },
    );

    stream.on('text', (delta: string) => {
      assistantText += delta;
      emit({
        type: 'delta',
        conversationId: args.conversationId,
        exchangeId: args.exchangeId,
        text: delta,
      });
    });

    const finalMessage = await stream.finalMessage();

    // SKIP detection: the system prompt says "Return only the literal
    // text SKIP". Models sometimes violate this and emit
    // `SKIP\n\n<reason>`. Treat any response that starts with SKIP as a
    // skip — otherwise the verbose-SKIP slips into history and the model
    // follows the pattern ("we're in SKIP mode for this convo") for
    // every subsequent turn, breaking real follow-up questions. Mirrors
    // the same fix in desktop's `src/main/coach.ts`.
    const trimmedAssistant = assistantText.trim();
    const isSkip =
      trimmedAssistant === 'SKIP' ||
      trimmedAssistant.startsWith('SKIP\n') ||
      trimmedAssistant.startsWith('SKIP ');
    if (trimmedAssistant && !isSkip) {
      let committedUser = trimmed;
      if (images.length > 0) {
        const stamp = new Date().toLocaleTimeString();
        const noun = images.length === 1 ? 'screenshot' : `${images.length} screenshots`;
        committedUser = `[${noun} at ${stamp}]`;
        if (trimmed) committedUser += ` ${trimmed}`;
      }
      convo.history.push({ role: 'user', content: committedUser });
      convo.history.push({ role: 'assistant', content: finalMessage.content });
    }

    // Normalise verbose-SKIP to the bare literal so the renderer's
    // exact-match check (`e.fullText.trim() === 'SKIP'`) lands on the
    // `status: 'skipped'` branch — otherwise `SKIP\n\n<reason>` would
    // slip past and render as a normal answer.
    emit({
      type: 'done',
      conversationId: args.conversationId,
      exchangeId: args.exchangeId,
      fullText: isSkip ? 'SKIP' : assistantText,
      cancelled: false,
    });
  } catch (err) {
    if (err instanceof Anthropic.APIUserAbortError) {
      emit({
        type: 'done',
        conversationId: args.conversationId,
        exchangeId: args.exchangeId,
        fullText: assistantText,
        cancelled: true,
      });
      return;
    }
    emit({
      type: 'error',
      conversationId: args.conversationId,
      exchangeId: args.exchangeId,
      message: friendlyError(err),
    });
  } finally {
    convo.inflight.delete(args.exchangeId);
  }
}

export function cancel(payload?: { conversationId: string; exchangeId?: string }): void {
  if (!payload) {
    for (const convo of conversations.values()) abortAll(convo);
    return;
  }
  const convo = conversations.get(payload.conversationId);
  if (!convo) return;
  if (payload.exchangeId) {
    const ctrl = convo.inflight.get(payload.exchangeId);
    if (ctrl) {
      ctrl.abort();
      convo.inflight.delete(payload.exchangeId);
    }
  } else {
    abortAll(convo);
  }
}

export function onClaudeEvent(handler: Listener): () => void {
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
}

export async function testKey(plaintext: string, model = 'claude-sonnet-4-6'): Promise<void> {
  const key = plaintext.trim() || keys.getAnthropic() || '';
  if (!key) throw new Error('No Anthropic API key set. Paste one or save it in Settings first.');
  const client = new Anthropic({ apiKey: key, dangerouslyAllowBrowser: true, baseURL: PROXY_BASE_URL });
  try {
    await client.messages.create({
      model,
      max_tokens: 1,
      messages: [{ role: 'user', content: 'ok' }],
    });
  } catch (err) {
    throw new Error(friendlyError(err));
  }
}

export async function listModels(): Promise<{ id: string; label: string }[]> {
  const apiKey = keys.getAnthropic();
  if (!apiKey) return [];
  const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true, baseURL: PROXY_BASE_URL });
  const page = await client.models.list();
  return page.data.map((m) => ({
    id: m.id,
    label: (m as { display_name?: string }).display_name ?? m.id,
  }));
}
